import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  FileText,
  User,
  Briefcase,
  GraduationCap,
  Clock,
  MoreVertical,
  ChevronRight,
  Plus,
  UserCheck,
  UserX,
  Download,
  ExternalLink,
  MessageSquare,
  CheckCircle2
} from 'lucide-react';
import {
  useApplication,
  useActivities,
  useAddActivity,
  useUpdateApplicationStage,
  useConvertToEmployee,
  useRecruiters,
} from '../../hooks/useHiring';
import ApplicationActivity from '../../components/hiring/ApplicationActivity';
import AddActivityModal from '../../components/hiring/AddActivityModal';
import HireApplicantModal from '../../components/hiring/HireApplicantModal';

const STAGES = [
  { id: 'new', label: 'New', color: 'bg-gray-100 text-gray-700', description: 'New application' },
  { id: 'screening', label: 'Screening', color: 'bg-blue-100 text-blue-700', description: 'Initial screening' },
  { id: 'interview', label: 'Interview', color: 'bg-purple-100 text-purple-700', description: 'Interview process' },
  { id: 'reference_check', label: 'Reference Check', color: 'bg-yellow-100 text-yellow-700', description: 'Checking references' },
  { id: 'offer', label: 'Offer', color: 'bg-orange-100 text-orange-700', description: 'Offer extended' },
  { id: 'hired', label: 'Hired', color: 'bg-green-100 text-green-700', description: 'Successfully hired' },
  { id: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700', description: 'Not selected' },
  { id: 'withdrawn', label: 'Withdrawn', color: 'bg-gray-100 text-gray-500', description: 'Applicant withdrew' },
];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'resume', label: 'Resume & Documents' },
  { id: 'references', label: 'References' },
  { id: 'interviews', label: 'Interview Notes' },
];

const ApplicationDetail = () => {
  const { tenantId, applicationId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showHireModal, setShowHireModal] = useState(false);
  const [showStageMenu, setShowStageMenu] = useState(false);

  // Fetch data
  const { data: application, isLoading: isLoadingApp, error: appError } = useApplication(applicationId);
  const { data: activities, isLoading: isLoadingActivities, error: activitiesError } = useActivities(applicationId);
  const { data: recruitersData } = useRecruiters();

  // Mutations
  const updateStageMutation = useUpdateApplicationStage();
  const addActivityMutation = useAddActivity();
  const convertToEmployeeMutation = useConvertToEmployee();

  const recruiters = recruitersData?.users || [];

  if (isLoadingApp) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (appError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        Failed to load application: {appError.message}
      </div>
    );
  }

  if (!application) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
        Application not found
      </div>
    );
  }

  const currentStage = STAGES.find(s => s.id === application.stage) || STAGES[0];
  // eslint-disable-next-line no-unused-vars
  const _currentStageIndex = STAGES.findIndex(s => s.id === application.stage);

  const handleStageChange = async (newStage) => {
    try {
      await updateStageMutation.mutateAsync({ id: applicationId, stage: newStage });
      setShowStageMenu(false);
    } catch (err) {
      console.error('Failed to update stage:', err);
    }
  };

  const handleAddActivity = async (data) => {
    try {
      await addActivityMutation.mutateAsync({ applicationId, data });
      setShowAddActivityModal(false);
    } catch (err) {
      console.error('Failed to add activity:', err);
    }
  };

  const handleHire = async (data) => {
    try {
      await convertToEmployeeMutation.mutateAsync({ applicationId, data });
      setShowHireModal(false);
      // Navigate to employees list or show success message
    } catch (err) {
      console.error('Failed to hire applicant:', err);
    }
  };

  const handleAssignRecruiter = async (recruiterId) => {
    // This would update the assigned recruiter
    console.log('Assign recruiter:', recruiterId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStageProgress = () => {
    if (application.stage === 'hired') return 100;
    if (application.stage === 'rejected' || application.stage === 'withdrawn') return 0;
    const progressStages = ['new', 'screening', 'interview', 'reference_check', 'offer'];
    const index = progressStages.indexOf(application.stage);
    return index >= 0 ? ((index + 1) / progressStages.length) * 100 : 0;
  };

  // Header with applicant info and actions
  const renderHeader = () => (
    <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        {/* Left: Applicant Info */}
        <div className="flex items-start space-x-4">
          <button
            onClick={() => navigate(`/${tenantId}/dashboard/hiring`)}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-2xl font-bold text-purple-700">
              {application.firstName?.[0]}{application.lastName?.[0]}
            </span>
          </div>
          
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {application.firstName} {application.lastName}
            </h1>
            <p className="text-gray-500">{application.positionApplied || 'Employee Application'}</p>
            
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
              <span className="flex items-center">
                <Mail size={14} className="mr-1.5" />
                {application.email}
              </span>
              {application.phone && (
                <span className="flex items-center">
                  <Phone size={14} className="mr-1.5" />
                  {application.phone}
                </span>
              )}
              <span className="flex items-center">
                <Clock size={14} className="mr-1.5" />
                Applied {formatDate(application.appliedAt || application.createdAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Stage Badge & Actions */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Stage Badge */}
          <div className="relative">
            <button
              onClick={() => setShowStageMenu(!showStageMenu)}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${currentStage.color} hover:opacity-90 transition-opacity`}
            >
              {currentStage.label}
              <ChevronRight size={16} className="ml-1" />
            </button>
            
            {showStageMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowStageMenu(false)} 
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 z-20 py-1">
                  {STAGES.map((stage) => (
                    <button
                      key={stage.id}
                      onClick={() => handleStageChange(stage.id)}
                      disabled={updateStageMutation.isPending}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between ${
                        stage.id === application.stage ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                      }`}
                    >
                      <span>{stage.label}</span>
                      {stage.id === application.stage && (
                        <CheckCircle2 size={16} />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          {application.stage !== 'hired' && application.stage !== 'rejected' && (
            <>
              <button
                onClick={() => setShowHireModal(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <UserCheck size={18} />
                <span>Hire</span>
              </button>
              
              <button
                onClick={() => handleStageChange('rejected')}
                className="px-4 py-2 bg-white text-red-600 font-medium rounded-lg border border-red-200 hover:bg-red-50 transition-colors flex items-center space-x-2"
              >
                <UserX size={18} />
                <span>Reject</span>
              </button>
            </>
          )}
          
          <button className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <MoreVertical size={20} />
          </button>
        </div>
      </div>

      {/* Stage Progress */}
      <div className="mt-6 pt-6 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
          <span>Application Progress</span>
          <span>{Math.round(getStageProgress())}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-purple-600 transition-all duration-500"
            style={{ width: `${getStageProgress()}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>New</span>
          <span>Screening</span>
          <span>Interview</span>
          <span>References</span>
          <span>Offer</span>
          <span>Hired</span>
        </div>
      </div>
    </div>
  );

  // Overview Tab Content
  const renderOverview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column: Profile Summary */}
      <div className="space-y-6">
        {/* Quick Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Contact Information</h3>
          <div className="space-y-3">
            <div className="flex items-start">
              <Mail size={16} className="text-gray-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <a href={`mailto:${application.email}`} className="text-sm text-purple-600 hover:underline">
                  {application.email}
                </a>
              </div>
            </div>
            {application.phone && (
              <div className="flex items-start">
                <Phone size={16} className="text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <a href={`tel:${application.phone}`} className="text-sm text-gray-900">
                    {application.phone}
                  </a>
                </div>
              </div>
            )}
            {application.address && (
              <div className="flex items-start">
                <MapPin size={16} className="text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="text-sm text-gray-900">{application.address}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Experience Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Experience</h3>
          {application.yearsOfExperience !== undefined ? (
            <div className="flex items-center mb-4">
              <Briefcase size={18} className="text-purple-600 mr-2" />
              <span className="font-medium">{application.yearsOfExperience} years</span>
            </div>
          ) : null}
          
          {application.previousExperience && application.previousExperience.length > 0 ? (
            <div className="space-y-3">
              {application.previousExperience.slice(0, 3).map((exp, idx) => (
                <div key={idx} className="border-l-2 border-purple-200 pl-3">
                  <p className="font-medium text-sm text-gray-900">{exp.title}</p>
                  <p className="text-sm text-gray-500">{exp.company}</p>
                  <p className="text-xs text-gray-400">{exp.duration}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No experience details provided</p>
          )}
        </div>

        {/* Education */}
        {application.education && application.education.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Education</h3>
            <div className="space-y-3">
              {application.education.map((edu, idx) => (
                <div key={idx} className="flex items-start">
                  <GraduationCap size={16} className="text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <p className="font-medium text-sm text-gray-900">{edu.degree}</p>
                    <p className="text-sm text-gray-500">{edu.institution}</p>
                    <p className="text-xs text-gray-400">{edu.year}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assigned Recruiter */}
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Assigned Recruiter</h3>
          <select
            className="form-input"
            value={application.assignedTo || ''}
            onChange={(e) => handleAssignRecruiter(e.target.value)}
          >
            <option value="">Unassigned</option>
            {recruiters.map((recruiter) => (
              <option key={recruiter.id} value={recruiter.id}>
                {recruiter.firstName} {recruiter.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Right Column: Activity Timeline */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Activity Timeline</h3>
            <button
              onClick={() => setShowAddActivityModal(true)}
              className="text-sm text-purple-600 hover:text-purple-700 flex items-center font-medium"
            >
              <Plus size={16} className="mr-1" />
              Add Activity
            </button>
          </div>
          
          <ApplicationActivity 
            activities={activities}
            isLoading={isLoadingActivities}
            error={activitiesError}
          />
        </div>
      </div>
    </div>
  );

  // Resume & Documents Tab
  const renderResume = () => (
    <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Resume & Documents</h3>
      
      {application.resumeUrl ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <FileText size={24} className="text-purple-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Resume</p>
                <p className="text-sm text-gray-500">PDF Document</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <a
                href={application.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <ExternalLink size={18} />
              </a>
              <a
                href={application.resumeUrl}
                download
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <Download size={18} />
              </a>
            </div>
          </div>
          
          {/* Preview iframe */}
          <div className="border rounded-lg overflow-hidden" style={{ height: '600px' }}>
            <iframe
              src={application.resumeUrl}
              title="Resume Preview"
              className="w-full h-full"
            />
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <FileText className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p>No resume uploaded</p>
        </div>
      )}

      {/* Other Documents */}
      {application.documents && application.documents.length > 0 && (
        <div className="mt-6 pt-6 border-t">
          <h4 className="font-medium text-gray-900 mb-4">Additional Documents</h4>
          <div className="space-y-2">
            {application.documents.map((doc, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <FileText size={18} className="text-gray-400 mr-3" />
                  <span className="text-sm text-gray-700">{doc.name}</span>
                </div>
                <a
                  href={doc.url}
                  download
                  className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                >
                  <Download size={18} />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // References Tab
  const renderReferences = () => (
    <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
      <h3 className="font-semibold text-gray-900 mb-4">References</h3>
      
      {application.references && application.references.length > 0 ? (
        <div className="space-y-4">
          {application.references.map((reference, idx) => (
            <div key={idx} className="border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                    <User size={18} className="text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{reference.name}</p>
                    <p className="text-sm text-gray-500">{reference.relationship}</p>
                    <div className="flex items-center mt-1 text-sm">
                      {reference.phone && (
                        <a href={`tel:${reference.phone}`} className="text-purple-600 hover:underline mr-3">
                          {reference.phone}
                        </a>
                      )}
                      {reference.email && (
                        <a href={`mailto:${reference.email}`} className="text-purple-600 hover:underline">
                          {reference.email}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                
                {reference.checked && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                    <CheckCircle2 size={12} className="mr-1" />
                    Checked
                  </span>
                )}
              </div>
              
              {reference.notes && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-600">{reference.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <User className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p>No references provided</p>
        </div>
      )}
    </div>
  );

  // Interview Notes Tab
  const renderInterviews = () => {
    const interviewActivities = activities?.filter(a => a.type === 'interview') || [];
    
    return (
      <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Interview Notes</h3>
          <button
            onClick={() => setShowAddActivityModal(true)}
            className="px-3 py-2 min-h-[44px] text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 flex items-center font-medium rounded-lg"
          >
            <Plus size={16} className="mr-1" />
            Add Interview
          </button>
        </div>
        
        {interviewActivities.length > 0 ? (
          <div className="space-y-4">
            {interviewActivities.map((interview) => (
              <div key={interview.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{interview.title}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(interview.scheduledAt || interview.createdAt).toLocaleString()}
                    </p>
                    {interview.interviewType && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700 mt-2">
                        {interview.interviewType}
                      </span>
                    )}
                  </div>
                </div>
                
                {interview.interviewers && interview.interviewers.length > 0 && (
                  <p className="text-sm text-gray-600 mt-2">
                    Interviewers: {interview.interviewers.join(', ')}
                  </p>
                )}
                
                {interview.content && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{interview.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p>No interview notes yet</p>
            <button
              onClick={() => setShowAddActivityModal(true)}
              className="mt-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
            >
              Schedule an interview
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      {renderHeader()}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto pb-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="pb-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'resume' && renderResume()}
        {activeTab === 'references' && renderReferences()}
        {activeTab === 'interviews' && renderInterviews()}
      </div>

      {/* Modals */}
      <AddActivityModal
        isOpen={showAddActivityModal}
        onClose={() => setShowAddActivityModal(false)}
        onSubmit={handleAddActivity}
        isLoading={addActivityMutation.isPending}
      />

      <HireApplicantModal
        isOpen={showHireModal}
        onClose={() => setShowHireModal(false)}
        onSubmit={handleHire}
        applicant={application}
        isLoading={convertToEmployeeMutation.isPending}
      />
    </div>
  );
};

export default ApplicationDetail;
